import React from "react";
import { lxContext } from "../../Utils/context";
import { LxI18NProps } from "../LxI18N.model";
import { initializeI18n } from "./lx-i18n-initializeI";

export type LxI18NStatus = "initializing" | "ready" | "error";

export const [LxI18NProvider, useLxI18NProvider] = lxContext(
  (props?: LxI18NProps) => {
    const mounted = React.useRef(false);
    const { resolver, locale = "de-DE", options, namespaces } = props!;
    const [status, setStatus] = React.useState<LxI18NStatus>("initializing");
    const [currentLocale, setCurrentLocale] = React.useState<string>(locale);

    // Check if context is mounted
    React.useEffect(() => {
      mounted.current = true;
      return () => {
        mounted.current = false;
      };
    });

    const { nextI18N: i18nInstance, ready } = React.useMemo(
      () =>
        initializeI18n({
          locale,
          options,
          resolver,
          namespaces,
        }),
      [locale, options, resolver, namespaces]
    );

    //
    // Update the status as soon as all translations are loaded
    //
    // We don't want to test async tests. Hence, we don't want to wait for
    // the Promise to resolve (we don't want to wait for the async status to be ready).
    //
    // Hence, sometime a synchronous test is all done before the promise
    // is resolved and we would get a React mount error. Therefore, we have
    // to check whether the context is still mounted before setting the status.
    React.useEffect(() => {
      ready
        .then(() => {
          if (mounted.current) {
            i18nInstance.on("languageChanged", () =>
              setCurrentLocale(i18nInstance.language)
            );
            setStatus("ready");
          }
        })
        .catch(() => {
          if (mounted.current) {
            setStatus("error");
          }
        });

      return () => i18nInstance.off("languageChanged");
    }, [ready, i18nInstance]);

    return { i18nInstance, resolver, options, status, currentLocale, ready, testMode: props?.testMode };
  }
);
